import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { RuntimeConfigService } from '../../../services/runtime-config.service';
import { BehaviorSubject, merge, Observable, of, ReplaySubject } from "rxjs";
import { tap, catchError } from "rxjs/operators";
import { HybridStorageKeys, HybridStorageService } from "../../services/hybrid-storage.service";
import { UserService } from "../../../services/user-service.service";

@Injectable({
  providedIn: 'root'
})
export class DirectoryService {

  private apiUrl: string;
  private directoryUsersCache = new Map<string, BehaviorSubject<DirectoryResponse | null>>();

  constructor(
    private http: HttpClient,
    private runtimeConfig: RuntimeConfigService,
    private hybridStorageService: HybridStorageService,
    private userService: UserService
  ) {
    this.apiUrl = this.runtimeConfig.currentConfig.apiUrl;

    // Subscribe to config changes to update apiUrl
    this.runtimeConfig.config$.subscribe(config => {
      this.apiUrl = config.apiUrl;
    });
  }

  /**
   * Clear all in-memory directory caches.
   * Called during impersonation to ensure fresh data is loaded.
   */
  clearCaches(): void {
    this.directoryUsersCache.clear();
  }

  /**
   * Get a list of users to be listed in the directory for a certain school
   * Uses cache-first strategy: returns cached data immediately if available,
   * then fetches fresh data in the background and updates cache
   * @param schoolId
   */
  getDirectoryUsers(schoolId: number, isMessaging: boolean = false): Observable<DirectoryResponse | null> {
    if (!this.userService.CurrentUserProfile?.Roles.some(r => r.RoleName === 'AppAccess_Directory')) {
      return of(null);
    }

    const cacheKey = `${schoolId}_${isMessaging}`;

    // Per-call failure channel. The cache subject is shared app-wide (background
    // prefetches use it too), so a failed fetch must never error it - that would
    // terminate it for every subscriber, including a later Retry.
    const requestFailure = new ReplaySubject<never>(1);

    if (!this.directoryUsersCache.has(cacheKey)) {
      this.directoryUsersCache.set(cacheKey, new BehaviorSubject<DirectoryResponse | null>(null));
      // Load cached data first, then fetch fresh data
      this.loadCachedDirectoryUsers(cacheKey).then(() => {
        // After cache is checked, fetch fresh data in background
        this.fetchAndCacheDirectoryUsers(cacheKey, schoolId, isMessaging, requestFailure);
      });
    } else {
      // Cache subject already exists, just fetch fresh data
      this.fetchAndCacheDirectoryUsers(cacheKey, schoolId, isMessaging, requestFailure);
    }

    const cacheSubject = this.directoryUsersCache.get(cacheKey)!;
    return merge(cacheSubject.asObservable(), requestFailure);
  }

  /**
   * Warm the directory cache for a school in the background.
   * The fetch starts as soon as getDirectoryUsers is called, so there is nothing to
   * subscribe to - failures are logged by the fetch and later callers will retry.
   * @param schoolId
   */
  prefetchDirectoryUsers(schoolId: number): void {
    this.getDirectoryUsers(schoolId);
  }

  private async loadCachedDirectoryUsers(cacheKey: string): Promise<void> {
    try {
      const cachedData = await this.hybridStorageService.getItem<DirectoryResponse>(
        HybridStorageKeys.Directory_Users + cacheKey
      );

      if (cachedData) {
        const cacheSubject = this.directoryUsersCache.get(cacheKey);
        if (cacheSubject) {
          cacheSubject.next(cachedData);
        }
      }
    } catch (error) {
      console.error('Error loading cached directory users:', error);
    }
  }

  private fetchAndCacheDirectoryUsers(cacheKey: string, schoolId: number, isMessaging: boolean, requestFailure: ReplaySubject<never>): void {
    const url = `${this.apiUrl}/userprofile/GetDirectoryUsersAsync/${schoolId}${isMessaging ? '?isMessaging=true' : ''}`;
    this.http.get<DirectoryResponse>(url)
      .pipe(
        tap(response => {
          this.hybridStorageService.setItem(HybridStorageKeys.Directory_Users + cacheKey, response);

          const cacheSubject = this.directoryUsersCache.get(cacheKey);
          if (cacheSubject) {
            cacheSubject.next(response);
          }
          requestFailure.complete();
        }),
        catchError(error => {
          console.error('Error fetching directory users:', error);

          const cacheSubject = this.directoryUsersCache.get(cacheKey);

          // A 403 means the user lost access to this school's directory - drop the
          // cached copy (stored and in-memory). Any other failure (offline, 5xx, timeout)
          // is treated as transient and the cached copy is kept for offline use.
          if (error instanceof HttpErrorResponse && error.status === 403) {
            this.hybridStorageService.setItem(HybridStorageKeys.Directory_Users + cacheKey, null);
            cacheSubject?.next(null);
          }

          // Only surface the error to this request's caller, and only when there is
          // nothing to show - if cached data is already displayed, keep showing it.
          if (cacheSubject?.value) {
            requestFailure.complete();
          } else {
            requestFailure.error(error);
          }

          return of(null);
        })
      )
      .subscribe();
  }
}

export type DirectoryUser = {
  UserId: number,
  FirstName: string,
  LastName: string,
  Title: string,
  Type: 'Staff' | 'Guardian' | 'Student',
  IsActiveStatus?: number | null,
  IsPrimaryGuardian: boolean,
  CanGuardiansMessage: boolean,
  PositionDescription?: string | null,
  GradeLevel?: string | null,
  /** Set when the student's grade came from a school other than the viewed one. */
  OtherSchoolName?: string | null
}

export type DirectoryHousehold = {
  HouseholdId: number,
  Guardians: DirectoryUser[],
  Students: DirectoryUser[]
}

export type DirectoryResponse = {
  Guardians: DirectoryUser[],
  Staff: DirectoryUser[],
  Households: DirectoryHousehold[]
}
